import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public "Featured AI Tools" section shown on the pre-launch ComingSoon
 * page AND (later) the main Home page. Gives visitors immediate social
 * proof that real enterprise brands are already on theailedger.ai — even
 * before our own creators launch.
 *
 * Each card routes to its dedicated storefront at /featured/:slug where
 * the primary CTA handles the tracked outbound click.
 */
export default function PublicFeaturedSection() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/affiliate-partners?limit=12&homepage_only=true`);
        if (!cancelled) setPartners(res.data.partners || []);
      } catch {
        // Silently degrade — non-critical UI.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loaded || partners.length === 0) return null;

  return (
    <section className="container mx-auto px-6 md:px-12 py-16 md:py-20 max-w-6xl" data-testid="public-featured-section">
      <div className="text-center mb-10 md:mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 mb-4">
          <Sparkles className="w-3.5 h-3.5 text-[#00F0FF]" />
          <span className="text-xs text-[#00F0FF] font-semibold tracking-wider">
            FEATURED AI TOOLS — AVAILABLE NOW
          </span>
        </div>
        <p className="text-neutral-400 max-w-2xl mx-auto">
          Hand-picked AI tools our customers can try today, with exclusive perks.
          More brands added weekly.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {partners.map((p) => (
          <button
            key={p.slug}
            onClick={() => navigate(`/featured/${p.slug}`)}
            className="group text-left bg-neutral-900/50 border border-white/10 hover:border-[#00F0FF]/50 rounded-2xl p-6 transition-all"
            data-testid={`public-featured-card-${p.slug}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-14 h-14 rounded-xl bg-black/60 border border-white/5 flex items-center justify-center overflow-hidden p-2">
                {p.logo_url ? (
                  <img
                    src={p.logo_url}
                    alt={p.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <Sparkles className="w-6 h-6 text-[#00F0FF]" />
                )}
              </div>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#00F0FF]">
                <Sparkles className="w-3 h-3" /> Featured
              </span>
            </div>
            <h3 className="text-xl font-bold mb-1 group-hover:text-[#00F0FF] transition-colors">
              {p.name}
            </h3>
            {p.tagline && (
              <p className="text-xs uppercase tracking-wider text-neutral-500 mb-3">
                {p.tagline}
              </p>
            )}
            {p.description && (
              <p className="text-sm text-neutral-400 line-clamp-3 min-h-[3.6em]">
                {p.description}
              </p>
            )}
            {p.customer_benefit && (
              <div className="mt-4 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-[#00FF94]/10 border border-[#00FF94]/30 text-[#00FF94] font-semibold">
                <Check className="w-3 h-3" /> {p.customer_benefit}
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
              <span className="text-xs text-neutral-500">{p.category}</span>
              <span className="text-xs text-[#00F0FF] group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                View details <ArrowRight className="w-3 h-3" />
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Tasteful footer disclosure */}
      <p className="mt-8 text-center text-[11px] text-neutral-600 max-w-2xl mx-auto" data-testid="public-featured-disclosure">
        theailedger.ai may earn a commission when you sign up for a featured tool
        through our links, at no extra cost to you.
      </p>
    </section>
  );
}
